import React, { FC, useContext, useState } from "react";

import { ComponentProps } from "~/types/components";
import { SearchResultSwatch } from "~/types/search";

import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";
import { FilterContext } from "~/providers/FilterProvider";

export type FilterSectionSwatchInputProps = ComponentProps & {
  options: SearchResultSwatch[];
  value: Record<string, boolean>;
  handleChange: (key: string) => void;
};

export type FilterSectionSwatchOutputProps = Omit<
  FilterSectionSwatchInputProps,
  "options" | "value"
> & {
  options: {
    count: number;
    key: string;
    checked: boolean;
    swatch: {
      colour: string | null;
      image: string | null;
    };
  }[];
  showMore: boolean;
  handleShowMore: () => void;
};

export const withFilterSectionSwatch =
  (Component: FC<FilterSectionSwatchOutputProps>) =>
  ({
    name = "FilterSectionSwatch",
    options,
    value,
    handleChange: handleChangeProp,
  }: FilterSectionSwatchInputProps) => {
    const { swatches } = useRouteLoaderData("root");
    const { handleScroll } = useContext(FilterContext);

    const [showMore, setShowMore] = useState(false);

    const swatchOptions = options
      ?.sort((a, b) => a?.key?.localeCompare(b?.key))
      ?.map(({ key, doc_count }) => ({
        key,
        checked: value[key],
        doc_count,
        swatch: swatches.find((swatch) =>
          Array.isArray(swatch.linked)
            ? swatch.linked.includes(key.toLowerCase())
            : false,
        ),
      }));

    if (!swatchOptions) {
      return null;
    }

    const mappedOptions = swatchOptions.map(
      ({ key, swatch, doc_count, checked }) => ({
        key,
        count: doc_count,
        checked,
        swatch: {
          colour: swatch?.colour,
          image: swatch?.image,
        },
      }),
    );

    const handleShowMore = () => {
      setShowMore(!showMore);
    };

    const handleChange = (key: string) => {
      handleScroll();
      handleChangeProp(key);
    };

    Component.displayName = name;
    return (
      <Component
        options={mappedOptions}
        handleChange={handleChange}
        showMore={showMore}
        handleShowMore={handleShowMore}
      />
    );
  };
